import type { ColDef, ICellRendererParams } from "ag-grid-community";
import classNames from "classnames";
import * as React from "react";

import type { TreeRowPlacement } from "../treeRows";

import { TreeCell } from "./TreeCell";
import styles from "./TreeCell.module.css";

interface TreeConfig<TData> {
  getPlacement: (row: TData) => TreeRowPlacement;
}

/** What AG Grid would draw for a column with no cell renderer. */
function plainValue<TData>(
  params: ICellRendererParams<TData>,
): React.ReactNode {
  const value: unknown = params.valueFormatted ?? params.value;
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }
  return null;
}

/**
 * `column` as a tree's first column: its cell is indented by the row's depth,
 * with a group's chevron (or, on a leaf, an empty slot of the same width)
 * ahead of what the column draws, and "continued" after it on a group a page
 * repeats. With no groups in the tree, the cell is drawn as the column would
 * draw it, so names line up with the header.
 */
export function treeColumn<TData>(
  column: ColDef<TData>,
  treeRef: React.RefObject<TreeConfig<TData> | undefined>,
  hasGroups: boolean,
): ColDef<TData> {
  if (!hasGroups) {
    return column;
  }
  const Inner = column.cellRenderer as
    React.ComponentType<ICellRendererParams<TData>> | undefined;
  return {
    ...column,
    cellClass: classNames(
      column.cellClass as string | undefined,
      styles.treeColumn,
    ),
    headerClass: classNames(
      column.headerClass as string | undefined,
      styles.treeHeader,
    ),
    // The row's place in the tree (its chevron, whether it is open) is not
    // part of the cell's value, and AG Grid leaves a cell whose value holds
    // as it is. Drawn again with each new row, the cell follows a group as it
    // opens and closes.
    equals: () => false,
    cellRenderer: (params: ICellRendererParams<TData>) => {
      const row = params.data;
      const placement = row ? treeRef.current?.getPlacement(row) : undefined;
      const content = Inner ? <Inner {...params} /> : plainValue(params);
      if (!placement) {
        return content;
      }
      return (
        <TreeCell placement={placement} cell={params.eGridCell}>
          {content}
        </TreeCell>
      );
    },
  };
}
